import React, { useEffect } from 'react';
import cx from 'classnames';
import { MDText } from 'i18n-react';
import Link from 'next/link';

import { Badge, DotList } from '@motorway/mw-highway-code';
import { VRM } from '@motorway/mw-highway-code/alpha';

import LazyLoadImage from 'Components/shared/LazyLoadImage/LazyLoadImage';
import { ShortlistButton } from 'Components/shared/ShortlistButton/ShortlistButton';
import VehicleCardGallery from 'Components/shared/VehicleCards/VehicleCard/VehicleCardGallery/VehicleCardGallery';
import { useBidCount } from 'Components/shared/VehicleCards/VehicleCard/VehicleCardView/VehicleCardView.helpers';
import { useBidding } from 'Context/BiddingContext';
import { triggerVehicleCardClickEvent, triggerVehicleShortlistedEvent } from 'Services/analytics/events';
import { useTriggerVehicleCardClickedEvent } from 'Services/optimizely/events/useTriggerVehicleCardClickedEvent';
import { useIsShortlisted } from 'Stores/ShortlistStore/ShortlistStore';
import {
	autoShorthand,
	formatDistance,
	formatMake,
	formatText,
	formatTransmission,
	fuelTypeShorthand,
} from 'Utilities/formatters';
import { applyTestIdData } from 'Utilities/helpers';
import { formatVehicleOriginalPrice } from 'Utilities/helpers/formatVehicleOriginalPrice';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import ROUTES from 'Utilities/routes';

import texts from '../VehicleListV2.texts.json';

import type { VehicleCardV2Props } from './VehicleCardV2.types';

import styles from './VehicleCardV2.module.scss';

const LocalT = new MDText(texts.vehicleCard);

export const VehicleCardV2: React.FC<VehicleCardV2Props> = ({ vehicle }) => {
	const {
		displayPrice,
		distance,
		fuel,
		hasEarlyDocuments,
		heroImages,
		id,
		isPaidListing,
		leadPhoto,
		make,
		mileage,
		model,
		numericGrade,
		originalPrice,
		prettyVrm,
		sourcePartner,
		state,
		transmission,
		year,
	} = vehicle;

	const isVehicleListHeroImagesEnabled = useFeatureToggle(FEATURES.vehicleListHeroImages);
	const triggerVehicleCardClickedEvent = useTriggerVehicleCardClickedEvent();
	const isShortlisted = useIsShortlisted(id);
	const { subscribeVehicleId } = useBidding();

	useEffect(() => {
		subscribeVehicleId({ id, stateSlug: state.slug }, { append: true });
	}, [id, state.slug, subscribeVehicleId]);

	const { bidCount, shouldShowBidCount } = useBidCount({ id, state });

	const isMobilizeVehicle = sourcePartner?.toLowerCase() === 'mobilize';
	const formattedPrice = `£${formatText(displayPrice)}`;
	const formattedOriginalPrice = formatVehicleOriginalPrice({
		originalPrice,
		price: displayPrice,
		slug: state.slug,
	});

	const onCardClick = () => {
		triggerVehicleCardClickedEvent(state.slug);
		triggerVehicleCardClickEvent(id);
	};

	const onClickShortlist = ({ id: vehicleId, isChecked, page }: { id: number; isChecked: boolean; page: string }) => {
		triggerVehicleShortlistedEvent({ id: vehicleId, isChecked, page });
	};

	const vehicleName = `${formatMake(make)} ${model}`;
	const headingId = `vehicle-${id}-heading`;

	return (
		<li className={styles.card} {...applyTestIdData('vehicle-card-v2')}>
			<article aria-labelledby={headingId} className={styles.article}>
				<div className={styles.imageWrapper}>
					<div className={styles.imageArea}>
						{isVehicleListHeroImagesEnabled && heroImages && heroImages.length > 1 ? (
							<VehicleCardGallery
								enquiryId={id}
								heroImages={heroImages}
								leadPhoto={leadPhoto}
								vehiclePhotoAlt={`${vehicleName} ${year}`}
							/>
						) : (
							<LazyLoadImage alt={`${vehicleName} ${year}`} photo={leadPhoto} rootComponent="VehicleCardV2" />
						)}
					</div>
					<div className={styles.imageBadges}>
						<VRM size="small" text={prettyVrm} />
						{!!distance && <Badge text={formatDistance(distance)} variant="default" />}
					</div>
				</div>

				<h3 className={styles.heading} id={headingId}>
					<Link
						data-thc-style-override
						as={ROUTES.VEHICLE.as(id)}
						className={styles.cardLink}
						href={`${ROUTES.VEHICLE.href}?vehicleId=${id}`}
						onClick={onCardClick}
					>
						<span className={styles.make}>{formatMake(make)}</span>
						<span className={styles.model}>{model}</span>
					</Link>
				</h3>

				<div className={styles.specs}>
					<DotList>
						{year && <li>{year}</li>}
						<li>{`${formatText(mileage)} ${LocalT.translate('mileageUnit')}`}</li>
						<li>{fuelTypeShorthand(fuel)}</li>
						<li>{autoShorthand(formatTransmission(transmission))}</li>
						{numericGrade && <li>{LocalT.translate('grade', { grade: numericGrade })}</li>}
					</DotList>
				</div>

				<div className={styles.priceRow}>
					<div className={styles.priceBlock}>
						<span className={styles.price}>{formattedPrice}</span>
						{formattedOriginalPrice && <span className={styles.originalPrice}>{formattedOriginalPrice}</span>}
					</div>
					{shouldShowBidCount && (
						<span className={styles.bidCount}>{LocalT.translate('bids', { count: bidCount })}</span>
					)}
				</div>

				<div className={styles.attributes}>
					{isPaidListing && <Badge text={LocalT.translate('paid')} variant="primary" />}
					{hasEarlyDocuments && (
						<div className={styles.badgeDocsReady}>
							<Badge text={LocalT.translate('docsReady')} variant="default" />
						</div>
					)}
					{isMobilizeVehicle && (
						<div className={styles.badgeMobilize}>
							<Badge text={LocalT.translate('mobilize')} variant="default" />
						</div>
					)}
				</div>
			</article>
			<div className={cx(styles.shortlistArea, { [styles.shortlistVisible]: isShortlisted })}>
				<ShortlistButton rounded id={id} onClickShortlist={onClickShortlist} page="vehicle list" />
			</div>
		</li>
	);
};
